import React from "react";
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  Switch,
  Divider,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import { enUS } from "@mui/x-date-pickers/locales";

const commonLocaleText = {
  ...enUS,
  fieldMonthPlaceholder: (params: any) =>
    params.contentType === "letter" ? "MMM" : "MM",
};

interface DeadlineSelectorProps {
  deadlineDate: string;
  deadlineOffset: string;
  setDeadlineToNull?: boolean;
  openDeadlinePicker?: boolean;
  onOpenDeadlinePickerChange?: () => void;
  onCloseDeadlinePickerChange?: () => void;
  onDateChange: (date: string) => void;
  onOffsetChange: (offset: string) => void;
  onSetDeadlineToNullChange?: (checked: boolean) => void;
}

const DeadlineSelector: React.FC<DeadlineSelectorProps> = ({
  deadlineDate,
  deadlineOffset,
  setDeadlineToNull = false,
  openDeadlinePicker = false,
  onOpenDeadlinePickerChange,
  onCloseDeadlinePickerChange,
  onDateChange,
  onOffsetChange,
  onSetDeadlineToNullChange,
}) => {
  return (
    <Box>
      <Box
        sx={{
          p: 2,
          border: "1px solid #EAECF0",
          borderRadius: "12px",
          boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.06)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontSize: 18,
              fontWeight: 600,
              mb: 1,
              lineHeight: "28px",
            }}
          >
            Set a deadline for this assignment
          </Typography>

          {deadlineDate && (
            <Button
              onClick={() => onDateChange("")}
              startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
              sx={{
                color: "#5354D8",
                fontSize: 16,
                "&:hover": {
                  background: "none",
                  opacity: 0.8,
                },
              }}
            >
              Clear deadline
            </Button>
          )}
        </Box>
        <Divider sx={{ borderColor: "#EAECF0" }} />

        <Box sx={{ mt: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography
              sx={{ fontSize: 14, color: "#344054", fontWeight: 500 }}
            >
              Deadline
            </Typography>
          </Box>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ width: "60%" }}>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                localeText={commonLocaleText}
              >
                <DatePicker
                  value={deadlineDate ? dayjs(deadlineDate) : null}
                  onChange={(date) => {
                    if (setDeadlineToNull) return;
                    const formattedDate = date
                      ? dayjs(date).format("DD-MMM-YYYY")
                      : "";
                    onDateChange(formattedDate);
                  }}
                  open={openDeadlinePicker}
                  onOpen={onOpenDeadlinePickerChange}
                  onClose={onCloseDeadlinePickerChange}
                  format="DD-MMM-YYYY"
                  disabled={setDeadlineToNull}
                  slots={{ openPickerIcon: CalendarTodayOutlinedIcon }}
                  slotProps={{
                    textField: {
                      size: "small",
                      variant: "outlined",
                      placeholder: "Select date",
                      disabled: setDeadlineToNull,
                      fullWidth: true,
                      error: false,
                      onClick: () => onOpenDeadlinePickerChange?.(),
                      InputProps: {
                        readOnly: true,
                        sx: {
                          "& .MuiInputAdornment-root button": {
                            border: "none",
                            padding: 0,
                            background: "transparent",
                            "&:hover": {
                              background: "transparent",
                            },
                          },
                        },
                      },
                      sx: {
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          height: "44px",
                        },
                      },
                    },
                    openPickerButton: { sx: { border: "none" } },
                    openPickerIcon: { sx: { width: "15px" } },
                  }}
                  sx={{
                    width: "100%",
                    "& .MuiSvgIcon-root": {
                      fontSize: "1.5rem",
                    },
                    "&. MuiPickersInputBase-root": {
                      flexDirection: "row-reverse",
                      padding: 0,
                      gap: "10px",
                    },
                    "&. MuiInputAdornment-root": {
                      padding: 0,
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#344054",
                  fontSize: 14,
                  whiteSpace: "nowrap",
                  height: "44px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                plus
              </Typography>
              <TextField
                size="small"
                type="number"
                placeholder="..."
                value={deadlineOffset}
                onChange={(e) => {
                  if (setDeadlineToNull) return;
                  const value = e.target.value;
                  // Allow empty string or valid non-negative numbers
                  if (
                    value === "" ||
                    (!isNaN(Number(value)) && Number(value) >= 0)
                  ) {
                    onOffsetChange(value);
                  }
                }}
                onBlur={(e) => {
                  // Ensure value is not negative on blur
                  const value = e.target.value;
                  if (value !== "" && Number(value) < 0) {
                    onOffsetChange("0");
                  }
                }}
                disabled={setDeadlineToNull}
                inputProps={{
                  min: 0,
                  step: 1,
                }}
                sx={{
                  flex: 1,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    height: "44px",
                  },
                  "& input": {
                    color: deadlineOffset ? "#101828" : "#667085",
                    fontSize: 14,
                    textAlign: "left",
                    padding: "8px",
                  },
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: "#4B5563",
                  fontSize: 14,
                  whiteSpace: "nowrap",
                  height: "44px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                days
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Box>
      {onSetDeadlineToNullChange && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            pt: 3,
          }}
        >
          <Typography
            sx={{
              color: "#344054",
              fontSize: 14,
              fontWeight: 500,
              lineHeight: 1.5,
              mr: 1,
            }}
          >
            Set deadline to null
          </Typography>
          <Switch
            checked={!!setDeadlineToNull}
            onChange={(e) => {
              if (onSetDeadlineToNullChange) {
                onSetDeadlineToNullChange(e.target.checked);
              }
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default DeadlineSelector;
